import { handleTransition } from 'Util/transitions.js';
import PCHelpers from '../components/postcode_check/postcodeCheckHelpers';
import VZSpinner from 'Components/VZSpinner';
import { tryParseJSON } from 'app_vodafone_ziggo/util/util';

const spinner = document.querySelector('vz-spinner') || new VZSpinner();

export default class VZBillingAddress extends HTMLElement {
    constructor() {
        super();
        this.checkbox = this.querySelector('.js-billing-address-toggle');
        this.checkbox?.addEventListener('change', this.toggleAddress.bind(this));

        // FORM ELEMENTS
        this.postcodeInput = this.querySelector('.js-billing-address-postcode');
        this.houseNumberInput = this.querySelector('.js-billing-address-housenumber');
        this.additionInput = this.querySelector('.js-billing-address-addition');
        this.additionSuggestions = this.querySelector('.js-addition-suggestions');
        this.address1Input = this.querySelector('.js-billing-address-address1');
        this.cityInput = this.querySelector('.js-billing-address-city');

        this.selectBillingAddress = document.querySelector('.js-select-billing-address');

        // MESSAGES
        this.addressNotFoundMessage = document.querySelector('.js-address-not-found-message');

        // FORM ATTRIBUTES
        this.checkoutForm = this.closest('.js-checkout-form');
        this.addressCheckEndpoint = this.checkoutForm.dataset?.addressCheckUrl;

        // Check if address1Input and cityInput are prefilled and adjust attributes
        [this.address1Input, this.cityInput].forEach((input) => {
            if (input.value.trim() !== '') {
                input.removeAttribute('disabled');
                input.setAttribute('readonly', true);
            }
        });

        // Event listener for the address lookup
        [this.postcodeInput, this.houseNumberInput, this.additionInput].forEach((field) => {
            // Formatting of the house number field
            if (field.type === 'number') {
                field.addEventListener('input', PCHelpers.respectMaxLength);
                field.addEventListener('input', PCHelpers.stripLeadingZero);
                field.addEventListener('keydown', PCHelpers.preventDashDotComma);
            }

            field.addEventListener('change', this.checkFieldsAndLookupAddress.bind(this));
        });

        // Event listener for the manual street and city input
        [this.address1Input, this.cityInput].forEach((field) => {
            field.addEventListener('change', this.manualAddressInput.bind(this));
        });

        this.selectBillingAddress?.addEventListener('change', this.handleBillingAddressSelection.bind(this));
    }

    /**
     * Toggles the display between the state if the billing address is the same as a delivery address or a different one.
     */
    toggleAddress() {
        const selectAddressDiv = this.querySelector('.js-select-address');
        const enterAddressDiv = this.querySelector('.js-enter-address');

        handleTransition(() => {
            if (this.checkbox.checked) {
                this.showSelectionOfAddresses(selectAddressDiv, enterAddressDiv);
            } else {
                this.showAddressForm(selectAddressDiv, enterAddressDiv);
            }
        });
    }

    /**
     * Displays options for selecting a delivery address to use as the billing address.
     *
     * @param {HTMLElement} selectAddressDiv - The container element for selecting an address.
     * @param {HTMLElement} enterAddressDiv - The container element for entering an address manually.
     */
    showSelectionOfAddresses(selectAddressDiv, enterAddressDiv) {
        enterAddressDiv.classList.add('d-none');
        selectAddressDiv.classList.remove('d-none');

        // Prefill address fields if there's only one delivery address
        if (document.querySelector('.js-single-delivery-address')) {
            const singleAddressDiv = document.querySelector('.js-single-delivery-address');
            const postCode = singleAddressDiv.dataset.postcode;
            const houseNumber = singleAddressDiv.dataset.housenumber;
            const addition = singleAddressDiv.dataset.addition;
            const address1 = singleAddressDiv.dataset.street;
            const city = singleAddressDiv.dataset.city;

            this.prefillBillingAddress(postCode, houseNumber, addition, address1, city);
        } else if (document.querySelector('.js-multiple-delivery-addresses')) {
            // Ensure the select field is initially unselected
            this.selectBillingAddress.value = '';
            this.selectBillingAddress.classList.remove('valid');
            this.selectBillingAddress.addEventListener('change', this.handleBillingAddressSelection.bind(this));
        }
    }

    /**
     * Handles the selection of a billing address from the dropdown.
     */
    handleBillingAddressSelection() {
        const selectedAddressValue = this.selectBillingAddress.value;
        const addressObj = tryParseJSON(selectedAddressValue);
        this.prefillBillingAddress(addressObj.postCode, addressObj.houseNumber, addressObj.appendix, addressObj.street, addressObj.city);
    }

    /**
     * Displays the form for manually entering an address and hides the address selection div.
     *
     * @param {HTMLElement} selectAddressDiv - The container element for selecting an address.
     * @param {HTMLElement} enterAddressDiv - The container element for entering an address manually.
     */
    showAddressForm(selectAddressDiv, enterAddressDiv) {
        enterAddressDiv.classList.remove('d-none');
        selectAddressDiv.classList.add('d-none');

        // Remove the address not found message
        if (!this.addressNotFoundMessage.classList.contains('d-none')) {
            this.addressNotFoundMessage.classList.add('d-none');
        }
        if (!this.additionSuggestions.classList.contains('d-none')) {
            this.additionSuggestions.classList.add('d-none');
        }

        this.resetAddressFields();
    }

    /**
     * Resets the values of the address input fields and updates their status.
     */
    resetAddressFields() {
        // Reset the address fields for postcode, house number, and addition
        [this.postcodeInput, this.houseNumberInput, this.additionInput].forEach((input) => {
            input.value = '';
            input.setFieldStatus();
        });
        // Reset the address fields for address line 1 and city, and adjust attributes
        [this.address1Input, this.cityInput].forEach((input) => {
            input.value = '';
            input.setFieldStatus();
            input.setAttribute('disabled', '');
            input.removeAttribute('readonly');
        });
    }

    /**
     * Prefills the billing address form fields with the provided address details and updates their status.
     *
     * @param {string} postCode - The postal code of the billing address.
     * @param {string} houseNumber - The house number of the billing address.
     * @param {string} [addition] - An optional addition to the house number.
     * @param {string} address1 - The first line of the street address.
     * @param {string} city - The city of the billing address.
     */
    prefillBillingAddress(postCode, houseNumber, addition, address1, city) {
        this.postcodeInput.value = postCode;
        this.houseNumberInput.value = houseNumber;
        this.additionInput.value = addition || '';
        this.address1Input.value = address1;
        this.cityInput.value = city;

        [this.postcodeInput, this.houseNumberInput, this.additionInput, this.cityInput, this.address1Input].forEach((input) => {
            input.setFieldStatus();
            input.removeAttribute('disabled');
            input.removeAttribute('readonly');
            input.dispatchEvent(new CustomEvent('input'));
        });
    }

    /**
     * Checks if the postcode and house number fields are filled and valid, then initiates an address lookup.
     */
    checkFieldsAndLookupAddress() {
        // Start address lookup when postcode and housenumber are filled and valid
        const allFieldsFilledAndValid = [this.postcodeInput, this.houseNumberInput].every((field) => field.value.trim() !== '' && field.checkValidity());

        if (allFieldsFilledAndValid) {
            this.executeAddressLookup();
        }
    }

    /**
     * Hides the "address not found" message.
     */
    manualAddressInput() {
        handleTransition(() => {
            this.addressNotFoundMessage.classList.add('d-none');
        });
    }

    /**
     * Executes an address lookup by sending a request to the address check endpoint with the current checkout form data.
     *
     * @returns {Promise} A promise that resolves when an address is successfully found or a specific condition is met, and rejects if no address is found or an error occurs.
     */
    executeAddressLookup() {
        spinner.start();

        return new Promise((resolve, reject) => {
            PCHelpers.fetchAddressAndAvailability(this.addressCheckEndpoint, this.checkoutForm)
                .then((addressCheckResponseData) => {
                    const multipleExtensionsFound = addressCheckResponseData?.multipleExtensionsFound;

                    this.resetCityAndStreetFields();

                    // Check if the addition input value exists in the response
                    const additionValue = this.additionInput.value.trim();
                    const additionExistsInResponse = addressCheckResponseData?.addressInfo?.some((info) => info.extension === additionValue);

                    // If multiple additions are found
                    if (!addressCheckResponseData?.success && multipleExtensionsFound && !additionExistsInResponse) {
                        this.handleMultipleAdditions(addressCheckResponseData);
                        resolve();
                        // If addition is manually entered and exists in the response
                    } else if (!addressCheckResponseData?.success && multipleExtensionsFound && additionExistsInResponse) {
                        this.handleManuallyEnteredAddition(addressCheckResponseData, additionValue);
                        resolve();
                        // If no address is found
                    } else if (!addressCheckResponseData?.success) {
                        this.handleAddressNotFound();
                        reject(addressCheckResponseData?.serverErrors[0]);
                        // If address is found
                    } else {
                        this.handleSuccessfulAddressCheck(addressCheckResponseData);
                        resolve();
                    }
                })
                .catch(PCHelpers.handleErrors.bind(this))
                .finally(spinner.stop);
        });
    }

    /**
     * Handles the successful address check by hiding the "address not found" message and filling in the address fields.
     *
     * @param {Object} addressCheckResponseData - The data returned from the address check API call.
     */
    handleSuccessfulAddressCheck(addressCheckResponseData) {
        handleTransition(() => {
            this.addressNotFoundMessage.classList.add('d-none');
        });

        this.fillInAddress(addressCheckResponseData.address);
    }

    /**
     * Handles the scenario when an address is not found by showing a message and enabling the city and address input fields.
     */
    handleAddressNotFound() {
        // Show the address not found message
        handleTransition(() => {
            this.addressNotFoundMessage.classList.remove('d-none');
        });

        // Enable the city and address1 input fields for manual input
        [this.cityInput, this.address1Input].forEach((input) => {
            input.setFieldStatus();
            input.removeAttribute('disabled');
            input.removeAttribute('readonly');
        });
    }

    /**
     * Handles the manually entered addition by removing the "address not found" message and filling in the address fields.
     *
     * @param {Object} addressResponse - The response object containing address information.
     * @param {string} additionValue - The manually entered addition value to find in the address response.
     */
    handleManuallyEnteredAddition(addressResponse, additionValue) {
        // Remove the address not found message
        handleTransition(() => {
            this.addressNotFoundMessage.classList.add('d-none');
        });
        const selectedAddition = addressResponse.addressInfo.find((info) => info.extension === additionValue);
        if (selectedAddition) {
            this.fillInAddress(selectedAddition.addressParts);
        }
    }

    /**
     * Handles the display of multiple address additions by generating a list of suggestions.
     *
     * @param {Object} addressCheckResponseData - The data returned from the address check API call, containing potential address additions.
     */
    handleMultipleAdditions(addressCheckResponseData) {
        const suggestions = addressCheckResponseData?.addressInfo;
        const suggestionList = this.additionSuggestions.querySelector('.js-addition-suggestions-list');

        // Remove the address not found message
        handleTransition(() => {
            this.addressNotFoundMessage.classList.add('d-none');
        });

        // Clear previous suggestions
        suggestionList.innerHTML = '';

        // Generate and append new suggestions to the list
        suggestions.forEach((suggestion, index) => {
            const suggestionHTML = `
                <li class="addition-suggestions__item">
                    <button class="addition-suggestions__button link link-secondary" type="button" data-index="${index}">${suggestion.addressParts.houseNumber} ${suggestion.extension}</button>
                </li>
            `;
            suggestionList.insertAdjacentHTML('beforeend', suggestionHTML);
        });

        // Attach click event listeners to each suggestion button
        const buttons = suggestionList.querySelectorAll('.addition-suggestions__button');
        buttons.forEach((button) => {
            button.addEventListener('click', (event) => {
                const index = event.target.getAttribute('data-index');
                this.selectHouseNumberAddition(suggestions[index]);
            });
        });

        // Display the suggestions list
        handleTransition(() => {
            this.additionSuggestions.classList.remove('d-none');
        });
    }

    /**
     * Selects a house number addition from the suggestions and fills in the address form.
     *
     * @param {Object} addressResponse - The response object containing the selected address information.
     */
    selectHouseNumberAddition(addressResponse) {
        this.additionInput.value = addressResponse.extension;
        this.additionInput.setFieldStatus();

        this.fillInAddress(addressResponse.addressParts);
    }

    /**
     * Fills in the address form with the selected address details and updates the UI accordingly.
     *
     * @param {Object} addressResponse - The response object containing the selected address information.
     * @param {string} addressResponse.street - The street name of the selected address.
     * @param {string} addressResponse.city - The city of the selected address.
     * @param {string} addressResponse.extension - The house number extension of the selected address.
     */
    fillInAddress(addressResponse) {
        // Hide the suggestions
        if (!this.additionSuggestions.classList.contains('d-none')) {
            handleTransition(() => {
                this.additionSuggestions.classList.add('d-none');
            });
        }

        // Fill in the address form fields with the selected address details
        this.address1Input.value = addressResponse.street;
        this.cityInput.value = addressResponse.city;
        this.additionInput.value = addressResponse.extension || '';

        // Update the UI for the filled fields
        [this.cityInput, this.address1Input].forEach((input) => {
            input.setFieldStatus();
            input.setAttribute('readonly', true);
            input.removeAttribute('disabled');
        });

        // Update the field status for the house number extension field
        this.additionInput.setFieldStatus();
    }

    /**
     * Resets the values of the city and street address fields.
     */
    resetCityAndStreetFields() {
        [this.address1Input, this.cityInput].forEach((input) => {
            input.value = '';
            input.setFieldStatus();
            input.setAttribute('disabled', '');
            input.removeAttribute('readonly');
        });
    }
}

if (!window.customElements.get('vz-billing-address')) {
    window.customElements.define('vz-billing-address', VZBillingAddress);
}
